import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link2, Trash2 } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { blockerService } from '@/lib/api/blocker.service';
import { taskService } from '@/lib/api/task.service';
import { activityService } from '@/lib/api/activity.service';
import { goalService } from '@/lib/api/goal.service';
import { eventService } from '@/lib/api/event.service';
import { handleApiError } from '@/lib/api/client';
import {
  BLOCKED_ENTITIES,
  type BlockedEntity,
  type Blocker,
  type BlockerMode,
  type BlockerType,
} from '@/types/blocker.types';

type DisplayEntityType = BlockedEntity | BlockerType;

interface CreateBlockerForm {
  blockedEntity: BlockedEntity;
  blockedId: string;
  blockedEventStatus: string;
  blockerType: BlockerType;
  blockerId: string;
  blockerEventStatus: string;
  scheduledTime: string;
  mode: BlockerMode;
}

interface EventReference {
  title: string;
  status?: string;
}

const ALLOWED_BLOCKER_TYPES_BY_ENTITY: Record<BlockedEntity, BlockerType[]> = {
  goal: ['goal', 'task', 'event'],
  task: ['task', 'goal', 'event'],
  activity: ['activity', 'time'],
  event: ['task', 'goal', 'event'],
};

const ALLOWED_MODES_BY_ENTITY: Record<BlockedEntity, BlockerMode[]> = {
  goal: ['hard'],
  task: ['hard'],
  activity: ['soft'],
  event: ['hard'],
};

const EVENT_STATUS_OPTIONS = ['draft', 'planning', 'planned', 'active', 'completed', 'cancelled'] as const;

const toIsoOrUndefined = (value: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toISOString();
};

export const BlockerManagementPage: React.FC = () => {
  const { celebrationId } = useParams<{ celebrationId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['blockers', 'common']);

  const celebrationIdNum = useMemo(() => Number.parseInt(celebrationId || '', 10), [celebrationId]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<Blocker[]>([]);
  const [taskNamesById, setTaskNamesById] = useState<Record<number, string>>({});
  const [activityNamesById, setActivityNamesById] = useState<Record<number, string>>({});
  const [goalNamesById, setGoalNamesById] = useState<Record<number, string>>({});
  const [eventNamesById, setEventNamesById] = useState<Record<number, EventReference>>({});

  const [queryEntity, setQueryEntity] = useState<BlockedEntity>('task');
  const [queryBlockedId, setQueryBlockedId] = useState('');

  const [createForm, setCreateForm] = useState<CreateBlockerForm>({
    blockedEntity: 'task',
    blockedId: '',
    blockedEventStatus: 'planned',
    blockerType: 'task',
    blockerId: '',
    blockerEventStatus: 'active',
    scheduledTime: '',
    mode: 'hard',
  });

  const allowedBlockerTypes = useMemo(
    () => ALLOWED_BLOCKER_TYPES_BY_ENTITY[createForm.blockedEntity],
    [createForm.blockedEntity],
  );
  const allowedModes = useMemo(
    () => ALLOWED_MODES_BY_ENTITY[createForm.blockedEntity],
    [createForm.blockedEntity],
  );

  useEffect(() => {
    if (!Number.isFinite(celebrationIdNum) || celebrationIdNum <= 0) {
      return;
    }

    const loadEntityNameMaps = async () => {
      try {
        const [tasks, activities, goalInstances] = await Promise.all([
          taskService.getCelebrationTasks(celebrationIdNum),
          activityService.getActivitiesByCelebration(celebrationIdNum),
          goalService.getInstancesByEntity('celebration', celebrationIdNum),
        ]);
        const allEvents = await eventService.getEvents();
        const events = allEvents.filter((event) => event.celebration_id === celebrationIdNum);

        setTaskNamesById(
          tasks.reduce<Record<number, string>>((acc, task) => {
            acc[task.id] = task.title;
            return acc;
          }, {})
        );

        setActivityNamesById(
          activities.reduce<Record<number, string>>((acc, activity) => {
            acc[activity.id] = activity.title;
            return acc;
          }, {})
        );

        setGoalNamesById(
          goalInstances.reduce<Record<number, string>>((acc, goal) => {
            const goalName = goal.template?.name || `Goal ${goal.id}`;
            acc[goal.id] = goalName;
            return acc;
          }, {})
        );

        setEventNamesById(
          events.reduce<Record<number, EventReference>>((acc, event) => {
            acc[event.id] = {
              title: event.title,
              status: event.status,
            };
            return acc;
          }, {}),
        );
      } catch {
        // Keep graceful fallback to IDs when name lookups fail.
      }
    };

    void loadEntityNameMaps();
  }, [celebrationIdNum]);

  const formatEntityReference = (entityType: DisplayEntityType, entityId: number | null): string => {
    if (entityId === null || entityId === undefined) {
      return entityType === 'time' ? t('blockers:entity.timeblocker') : 'n/a';
    }

    if (entityType === 'task') {
      const taskName = taskNamesById[entityId];
      return taskName ? `${taskName} (#${entityId})` : `Task #${entityId}`;
    }

    if (entityType === 'activity') {
      const activityName = activityNamesById[entityId];
      return activityName ? `${activityName} (#${entityId})` : `Activity #${entityId}`;
    }

    if (entityType === 'goal') {
      const goalName = goalNamesById[entityId];
      return goalName ? `${goalName} (#${entityId})` : `Goal #${entityId}`;
    }

    if (entityType === 'event') {
      const eventRef = eventNamesById[entityId];
      return eventRef ? `${eventRef.title} (#${entityId})` : `Event #${entityId}`;
    }

    return `Time blocker #${entityId}`;
  };

  const loadBlockers = useCallback(async (blockedEntity: BlockedEntity, blockedIdRaw: string) => {
    const blockedId = Number.parseInt(blockedIdRaw, 10);
    if (!Number.isFinite(blockedId) || blockedId <= 0) {
      setError(t('blockers:validation.blockedIdRequired'));
      setBlockers([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const list = await blockerService.listByBlockedEntity(blockedEntity, blockedId);
      setBlockers(list);
    } catch (err: unknown) {
      setError(handleApiError(err));
      setBlockers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const blockedEntityParam = searchParams.get('blockedEntity');
    const blockedIdParam = searchParams.get('blockedId');

    const normalizedEntity: BlockedEntity = blockedEntityParam === 'goal' || blockedEntityParam === 'task' || blockedEntityParam === 'activity' || blockedEntityParam === 'event'
      ? blockedEntityParam
      : 'task';

    const blockedId = blockedIdParam ? Number.parseInt(blockedIdParam, 10) : Number.NaN;
    if (!Number.isFinite(blockedId) || blockedId <= 0) {
      return;
    }

    const blockedIdString = String(blockedId);
    setQueryEntity(normalizedEntity);
    setQueryBlockedId(blockedIdString);
    setCreateForm((prev) => ({
      ...prev,
      blockedEntity: normalizedEntity,
      blockedId: blockedIdString,
    }));

    void loadBlockers(normalizedEntity, blockedIdString);
  }, [searchParams, loadBlockers]);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();

    const blockedId = Number.parseInt(createForm.blockedId, 10);
    if (!Number.isFinite(blockedId) || blockedId <= 0) {
      setError(t('blockers:validation.blockedIdInvalid'));
      return;
    }

    const blockerId = createForm.blockerId ? Number.parseInt(createForm.blockerId, 10) : undefined;
    if (createForm.blockerId && (blockerId === undefined || !Number.isFinite(blockerId) || blockerId <= 0)) {
      setError(t('blockers:validation.blockerIdInvalid'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await blockerService.createBlocker({
        blocked_entity: createForm.blockedEntity,
        blocked_id: blockedId,
        blocked_event_status: createForm.blockedEntity === 'event' ? createForm.blockedEventStatus : undefined,
        blocker_type: createForm.blockerType,
        blocker_id: blockerId,
        blocker_event_status: createForm.blockerType === 'event' ? createForm.blockerEventStatus : undefined,
        scheduled_time: toIsoOrUndefined(createForm.scheduledTime),
        mode: createForm.mode,
      });

      setQueryEntity(createForm.blockedEntity);
      setQueryBlockedId(String(blockedId));
      await loadBlockers(createForm.blockedEntity, String(blockedId));
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    setError(null);

    try {
      await blockerService.deleteBlocker(id);
      if (queryBlockedId) {
        await loadBlockers(queryEntity, queryBlockedId);
      }
    } catch (err: unknown) {
      setError(handleApiError(err));
    }
  };

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs
        items={[
          { label: 'Home', to: '/home' },
          { label: 'Celebration', to: Number.isFinite(celebrationIdNum) ? `/celebrations/${celebrationIdNum}` : '/celebrations' },
          { label: 'Blockers' },
        ]}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center">
                <Link2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>{t('blockers:title')}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('blockers:subtitle')}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(Number.isFinite(celebrationIdNum) ? `/celebrations/${celebrationIdNum}/tasks` : '/celebrations')}
            >
              {t('blockers:backToTasks')}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('blockers:form.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-3" onSubmit={handleCreate}>
              <div className="space-y-1">
                <Label htmlFor="blockedEntity">{t('blockers:form.fields.blockedEntity')}</Label>
                <select
                  id="blockedEntity"
                  value={createForm.blockedEntity}
                  onChange={(e) => {
                    const blockedEntity = e.target.value as BlockedEntity;
                    const nextBlockerType = ALLOWED_BLOCKER_TYPES_BY_ENTITY[blockedEntity][0];
                    const nextMode = ALLOWED_MODES_BY_ENTITY[blockedEntity][0];
                    setCreateForm((prev) => ({
                      ...prev,
                      blockedEntity,
                      blockedEventStatus: blockedEntity === 'event' ? prev.blockedEventStatus : 'planned',
                      blockerType: nextBlockerType,
                      mode: nextMode,
                      blockerId: nextBlockerType === 'time' ? '' : prev.blockerId,
                      blockerEventStatus: nextBlockerType === 'event' ? prev.blockerEventStatus : 'active',
                      scheduledTime: nextBlockerType === 'time' ? prev.scheduledTime : '',
                    }));
                  }}
                  title="Select blocked entity"
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {BLOCKED_ENTITIES.map((entity) => (
                    <option key={entity} value={entity}>{entity}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="blockedId">{t('blockers:form.fields.blockedId')}</Label>
                <Input
                  id="blockedId"
                  value={createForm.blockedId}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, blockedId: e.target.value }))}
                  placeholder="e.g. 101"
                  inputMode="numeric"
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="blockerType">{t('blockers:form.fields.blockerType')}</Label>
                <select
                  id="blockerType"
                  value={createForm.blockerType}
                  onChange={(e) => {
                    const blockerType = e.target.value as BlockerType;
                    setCreateForm((prev) => ({
                      ...prev,
                      blockerType,
                      blockerId: blockerType === 'time' ? '' : prev.blockerId,
                      blockerEventStatus: blockerType === 'event' ? prev.blockerEventStatus : 'active',
                      scheduledTime: blockerType === 'time' ? prev.scheduledTime : '',
                    }));
                  }}
                  title="Select blocker type"
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {allowedBlockerTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {createForm.blockedEntity === 'event' && (
                <div className="space-y-1">
                <Label htmlFor="blockedEventStatus">{t('blockers:form.fields.blockedEventStatus')}</Label>
                  <select
                    id="blockedEventStatus"
                    value={createForm.blockedEventStatus}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, blockedEventStatus: e.target.value }))}
                    title="Status threshold blocked on the event"
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {EVENT_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              )}

              {createForm.blockerType === 'event' && (
                <div className="space-y-1">
                <Label htmlFor="blockerEventStatus">{t('blockers:form.fields.blockerEventStatus')}</Label>
                  <select
                    id="blockerEventStatus"
                    value={createForm.blockerEventStatus}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, blockerEventStatus: e.target.value }))}
                    title="Status required for blocker event"
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {EVENT_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-1">
                <Label htmlFor="blockerId">{t('blockers:form.fields.blockerIdOptional')}</Label>
                <Input
                  id="blockerId"
                  value={createForm.blockerId}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, blockerId: e.target.value }))}
                  placeholder="for non-time blockers"
                  inputMode="numeric"
                  disabled={createForm.blockerType === 'time'}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="scheduledTime">{t('blockers:form.fields.scheduledTime')}</Label>
                <Input
                  id="scheduledTime"
                  type="datetime-local"
                  value={createForm.scheduledTime}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, scheduledTime: e.target.value }))}
                  disabled={createForm.blockerType !== 'time'}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="blockMode">{t('blockers:form.fields.mode')}</Label>
                <select
                  id="blockMode"
                  value={createForm.mode}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, mode: e.target.value as BlockerMode }))}
                  title="Select blocker mode"
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  disabled={allowedModes.length === 1}
                >
                  {allowedModes.map((mode) => (
                    <option key={mode} value={mode}>{mode}</option>
                  ))}
                </select>
              </div>

              <Button type="submit" size="sm" disabled={submitting}>
                {submitting ? t('blockers:form.buttons.creating') : t('blockers:form.buttons.create')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t('blockers:list.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-2 mb-4">
              <div className="space-y-1">
                <Label htmlFor="queryBlockedEntity">{t('blockers:form.fields.blockedEntity')}</Label>
                <select
                  id="queryBlockedEntity"
                  value={queryEntity}
                  onChange={(e) => setQueryEntity(e.target.value as BlockedEntity)}
                  title="Select blocked entity for list"
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {BLOCKED_ENTITIES.map((entity) => (
                    <option key={entity} value={entity}>{entity}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="queryBlockedId">{t('blockers:form.fields.blockedId')}</Label>
                <Input
                  id="queryBlockedId"
                  value={queryBlockedId}
                  onChange={(e) => setQueryBlockedId(e.target.value)}
                  placeholder="e.g. 101"
                  inputMode="numeric"
                />
              </div>

              <Button
                type="button"
                size="sm"
                onClick={() => void loadBlockers(queryEntity, queryBlockedId)}
                disabled={loading}
              >
                {loading ? t('blockers:list.loading') : t('blockers:list.load')}
              </Button>
            </div>

            {blockers.length === 0 ? (
              <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                {t('blockers:list.empty')}
              </div>
            ) : (
              <div className="space-y-2">
                {blockers.map((blocker) => (
                  <div key={blocker.id} className="rounded-md border p-3">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-sm font-medium">
                          {formatEntityReference(blocker.blocked_entity, blocker.blocked_id)} {t('blockers:list.blockedBy')} {formatEntityReference(blocker.blocker_type, blocker.blocker_id)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          mode: {blocker.mode}
                          {blocker.blocked_entity === 'event' && blocker.blocked_event_status ? ` · blocks event status >= ${blocker.blocked_event_status}` : ''}
                          {blocker.blocker_type === 'event' && blocker.blocker_event_status ? ` · requires blocker event >= ${blocker.blocker_event_status}` : ''}
                          {' '}· type: {blocker.blocker_type} · scheduled: {blocker.scheduled_time || 'n/a'}
                        </p>
                      </div>

                      <Button type="button" variant="outline" size="sm" onClick={() => void handleDelete(blocker.id)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        {t('blockers:list.delete')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
};
